/**
 * Backend HTTP client — talks to partner-portal-api.
 *
 * Resolution order for the API base URL:
 *   1. `VITE_API_BASE_URL` build-time env (CI, staging overrides, etc.)
 *   2. Hard-coded prod URL when the bundle runs on the deployed CDN domain.
 *      Without this we'd silently boot in mock-mode whenever the env var
 *      is missing at build time — partners would see "Demo mód: nyisd meg
 *      a böngésző konzolt" on login despite the backend being live.
 *   3. Otherwise fall back to the in-memory mock (`./mock.ts`) — keeps
 *      `vite dev` working standalone for local dev / partner walk-throughs.
 */

import type {
  ApiError,
  DemoPartner,
  DemoPass,
  DesignImageUploadUrlBody,
  DesignImageUploadUrlResponse,
  FlightLookup,
  MintPassRequest,
  PassDesignDocDto,
  PassDesignPutBody,
  PassDesignSummaryDto,
  PassTemplateKind,
} from '../types';
import type { AppleStyle } from '../walletStyles';

import * as mock from './mock';

const PROD_HOSTS: Record<string, string> = {
  'wallet-demo-dev.insurwiz.io': 'https://api.wallet-demo-dev.insurwiz.io',
  // The clean apex URL is a CloudFront alias of the SAME dev dist; there is no
  // separate prod API, and `wallet-demo-api.insurwiz.io` was never provisioned
  // (no DNS) — so point it at the live dev API (CORS allows the clean origin).
  'wallet-demo.insurwiz.io': 'https://api.wallet-demo-dev.insurwiz.io',
};

const ENV_API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string | undefined;
const HOSTNAME_API_BASE_URL =
  typeof window !== 'undefined' ? PROD_HOSTS[window.location.hostname] : undefined;
const API_BASE_URL = ENV_API_BASE_URL || HOSTNAME_API_BASE_URL;
const USE_MOCK = !API_BASE_URL;

const TOKEN_KEY = 'walletDemo.jwt';

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearStoredToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Session-expiry hook: the auth layer registers a listener that runs when an
 * AUTHENTICATED request is answered 401 (the 7-day JWT expired or was revoked
 * under an open tab). Without it every page sat on raw "Unauthorized" errors
 * while the header still showed the partner signed in.
 */
let unauthorizedHandler: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiError,
  ) {
    super(body.message);
  }
}

async function request<T>(
  path: string,
  init: RequestInit & {
    auth?: boolean;
    /** false = a 401 here is not a session expiry (the explicit sign-out). */
    notifyUnauthorized?: boolean;
  } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    ...((init.headers as Record<string, string> | undefined) ?? {}),
  };
  if (init.auth) {
    const token = getStoredToken();
    if (!token)
      throw new ApiClientError(401, { code: 'NO_SESSION', message: 'Bejelentkezés szükséges' });
    headers['authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE_URL}${path}`, { ...init, headers });
  if (!res.ok) {
    let body: ApiError;
    try {
      body = (await res.json()) as ApiError;
    } catch {
      body = { code: 'UNKNOWN', message: `HTTP ${res.status}` };
    }
    if (init.auth && res.status === 401 && init.notifyUnauthorized !== false) {
      clearStoredToken();
      unauthorizedHandler?.();
    }
    throw new ApiClientError(res.status, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ─── Auth ────────────────────────────────────────────────────────────────────

/** Mock mode only: where the (e-mail-less) sign-in link points. */
export function mockSignInPath(): string | null {
  return USE_MOCK ? mock.pendingSignInPath() : null;
}

export async function requestMagicLink(email: string): Promise<{ ok: true }> {
  if (USE_MOCK) return mock.requestMagicLink(email);
  return request('/v1/portal/auth/request', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function verifyMagicLink(
  token: string,
): Promise<{ jwt: string; partner: DemoPartner }> {
  if (USE_MOCK) return mock.verifyMagicLink(token);
  return request('/v1/portal/auth/verify', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export async function getMe(): Promise<DemoPartner> {
  if (USE_MOCK) return mock.getMe();
  return request<DemoPartner>('/v1/portal/me', { method: 'GET', auth: true });
}

export async function signOut(): Promise<void> {
  if (USE_MOCK) return;
  // Best-effort server-side revoke. The local token is cleared by the
  // caller regardless, so a network failure here doesn't strand the user
  // logged in on this device — just on others using the same JWT.
  try {
    await request('/v1/portal/auth/sign-out', {
      method: 'POST',
      auth: true,
      notifyUnauthorized: false,
    });
  } catch {
    // swallow
  }
}

// ─── Flights ─────────────────────────────────────────────────────────────────

export async function lookupFlight(iata: string, date: string): Promise<FlightLookup> {
  if (USE_MOCK) return mock.lookupFlight(iata, date);
  return request<FlightLookup>(
    `/v1/portal/flights/lookup?iata=${encodeURIComponent(iata)}&date=${encodeURIComponent(date)}`,
    { method: 'GET', auth: true },
  );
}

// ─── Passes ──────────────────────────────────────────────────────────────────

export async function listPasses(): Promise<DemoPass[]> {
  if (USE_MOCK) return mock.listPasses();
  return request<DemoPass[]>('/v1/portal/passes', { method: 'GET', auth: true });
}

export async function getPass(id: string): Promise<DemoPass> {
  if (USE_MOCK) return mock.getPass(id);
  return request<DemoPass>(`/v1/portal/passes/${id}`, { method: 'GET', auth: true });
}

export async function mintPass(req: MintPassRequest): Promise<DemoPass> {
  if (USE_MOCK) return mock.mintPass(req);
  return request<DemoPass>('/v1/portal/passes', {
    method: 'POST',
    auth: true,
    body: JSON.stringify(req),
  });
}

export async function deletePass(id: string): Promise<void> {
  if (USE_MOCK) return mock.deletePass(id);
  return request(`/v1/portal/passes/${id}`, { method: 'DELETE', auth: true });
}

// ─── Pass Designs (sub-project 2) ─────────────────────────────────────────────

export async function listPassDesigns(): Promise<PassDesignSummaryDto[]> {
  if (USE_MOCK) return mock.listPassDesigns();
  return request<PassDesignSummaryDto[]>('/v1/portal/pass-designs', { method: 'GET', auth: true });
}

export async function getPassDesign(
  templateKind: PassTemplateKind,
  appleStyle: AppleStyle,
): Promise<PassDesignDocDto> {
  if (USE_MOCK) return mock.getPassDesign(templateKind, appleStyle);
  return request<PassDesignDocDto>(
    `/v1/portal/pass-designs/${encodeURIComponent(templateKind)}/${encodeURIComponent(appleStyle)}`,
    { method: 'GET', auth: true },
  );
}

export async function putPassDesign(
  templateKind: PassTemplateKind,
  appleStyle: AppleStyle,
  body: PassDesignPutBody,
): Promise<PassDesignDocDto> {
  if (USE_MOCK) return mock.putPassDesign(templateKind, appleStyle, body);
  return request<PassDesignDocDto>(
    `/v1/portal/pass-designs/${encodeURIComponent(templateKind)}/${encodeURIComponent(appleStyle)}`,
    { method: 'PUT', auth: true, body: JSON.stringify(body) },
  );
}

export async function requestDesignImageUploadUrl(
  body: DesignImageUploadUrlBody,
): Promise<DesignImageUploadUrlResponse> {
  if (USE_MOCK) return mock.requestDesignImageUploadUrl(body);
  return request<DesignImageUploadUrlResponse>('/v1/portal/design-images', {
    method: 'POST',
    auth: true,
    body: JSON.stringify(body),
  });
}

export { ApiClientError, USE_MOCK };
